<template>
    <div class="customer-table-filter">
        <!-- Filter Conditions Section -->
        <div class="filter-conditions">
            <div
                class="filter-row"
                v-for="(filter, index) in clone"
                :key="`filter-${index}-${filter.fieldId}-${filter.operator}`"
            >
                <!-- Field seçme CustomPicker'ı -->
                <CustomPicker
                    v-model="clone[index].fieldId"
                    :items="availableFields"
                    idKey="_id"
                    labelKey="name"
                    label="Alan"
                    :title="$t('message.field')"
                    :icon="getFieldIcon(filter.fieldId)"
                    style="width: 30%; margin-right: 8px"
                    @input="onFieldChange(filter, index)"
                />

                <!-- Operatör seçme CustomPicker'ı -->
                <CustomPicker
                    v-model="clone[index].operator"
                    :items="getOperators(filter)"
                    idKey="value"
                    labelKey="label"
                    label="Operatör"
                    title="Operator"
                    icon="filter_list"
                    style="width: 25%; margin-right: 8px"
                    :disabled="!filter.fieldId"
                    @input="onOperatorChange(filter, index)"
                />

                <!-- Value inputu -->
                <CustomPicker
                    v-if="isDropdownField(filter)"
                    :items="getFieldOptions(filter)"
                    idKey="value"
                    labelKey="label"
                    label="Alan"
                    title="Value"
                    icon="filter_list"
                    style="width: 30%; margin-right: 8px"
                    v-model="clone[index].value"
                    :multiple="isMultipleDropdownField(filter) || ['is_one_of', 'is_not_one_of'].includes(clone[index].operator)"
                />

                <CustomPicker
                    v-else-if="isUserField(filter) && !['empty', 'not_empty'].includes(clone[index].operator)"
                    v-model="clone[index].value"
                    :items="users"
                    idKey="_id"
                    labelKey="name"
                    label="Kullanıcı"
                    :title="$t('message.user')"
                    icon="person"
                    style="width: 30%; margin-right: 8px"
                />

                <LinkedRecordFilterButton
                    v-else-if="getValueInputComponent(filter) === 'LinkedRecordFilterButton'"
                    v-model="clone[index].value"
                    :field="getFieldById(clone[index].fieldId)"
                />
                <!-- String field'lar için is_one_of operatörü - Autocomplete -->
                <a-select
                    v-else-if="
                        !['empty', 'not_empty'].includes(clone[index].operator) &&
                        ['is_one_of', 'is_not_one_of'].includes(clone[index].operator) &&
                        isStringNonDropdownField(filter)
                    "
                    v-model="clone[index].value"
                    style="width: 30%; margin-right: 8px;min-width: 200px;"
                    :placeholder="$t('table.enterOrSelectValues')"
                    :disabled="!clone[index].operator"
                    mode="tags"
                    :token-separators="[',', ';']"
                    :filter-option="false"
                    show-search
                    @search="onSearchSuggestions"
                >
                    <a-select-option
                        v-for="suggestion in getFieldSuggestions(filter)"
                        :key="suggestion.value"
                        :value="suggestion.value"
                        :title="suggestion.fullValue"
                    >
                        {{ suggestion.label }}
                    </a-select-option>
                </a-select>

                <component
                    v-else-if="
                        !['empty', 'not_empty'].includes(clone[index].operator) &&
                        getValueInputComponent(filter) !== 'a-date-picker'
                    "
                    :is="getValueInputComponent(filter)"
                    v-model="clone[index].value"
                    :options="getFieldOptions(filter)"
                    style="width: 30%; margin-right: 8px"
                    :placeholder="'Value'"
                    :disabled="!clone[index].operator"
                    :multiple="['is_one_of', 'is_not_one_of'].includes(clone[index].operator)"
                ></component>

                <a-date-picker
                    v-else-if="
                        !['empty', 'not_empty'].includes(clone[index].operator) &&
                        getValueInputComponent(filter) === 'a-date-picker' &&
                        clone[index].operator !== 'between'
                    "
                    v-model="clone[index].value"
                    style="width: 30%; margin-right: 8px"
                    :placeholder="'Value'"
                    :disabled="!filter.operator"
                    :show-time="{ defaultValue: $moment().startOf('day') }"
                    :format="'YYYY-MM-DD HH:mm'"
                >
                    <span
                        v-if="(clone[index].value || '').toString().startsWith('template_')"
                        class="date-template-label"
                    >
                        {{
                            (clone[index].value || "").startsWith("template_")
                                ? $_.get(
                                      dateTemplates.find((t) => t.value === clone[index].value),
                                      "label"
                                  )
                                : clone[index].value
                        }}
                    </span>
                    <template slot="renderExtraFooter" v-if="['equal', 'not_equal'].includes(clone[index].operator)">
                        <div style="padding: 8px">
                            <div style="display: flex; flex-wrap: wrap; gap: 8px">
                                <a-button
                                    v-for="template in dateTemplates"
                                    :key="template.value"
                                    size="small"
                                    @click="selectDateTemplate(template.value, index)"
                                >
                                    {{ template.label }}
                                </a-button>
                            </div>
                        </div>
                    </template>
                </a-date-picker>

                <a-range-picker
                    v-else-if="
                        !['empty', 'not_empty'].includes(clone[index].operator) &&
                        getValueInputComponent(filter) === 'a-date-picker' &&
                        clone[index].operator === 'between'
                    "
                    v-model="clone[index].value"
                    style="width: 300px; margin-right: 8px"
                    :placeholder="['Başlangıç', 'Bitiş']"
                    :disabled="!filter.operator"
                    :show-time="{
                        defaultOpenValue: $moment().startOf('day'),
                        defaultValue: $moment().startOf('day'),
                        format: 'HH:mm',
                    }"
                    :defaultPickerValue="[$moment().startOf('month'), $moment().endOf('month')]"
                    :format="'YYYY-MM-DD HH:mm'"
                />

                <!-- Silme butonu -->
                <a-button type="danger" icon="delete" @click="removeFilter(index)" />
            </div>
        </div>

        <!-- Add Condition ve Apply Filter Butonları -->
        <div class="filter-actions">
            <div class="">
                <a-button type="dashed" icon="plus" @click="addFilter"> {{ $t("tables.addCondition") }}</a-button>
            </div>
            <div class="right">
                <SupsisButton ghost @click="cancel" style="float: right"> {{ $t("message.cancel") }}</SupsisButton>
                <SupsisButton primary @click="applyFilter" style="float: right">
                    {{ $t("tables.applyFilter") }}
                </SupsisButton>
            </div>
        </div>
    </div>
</template>

<script>
import Vue from "vue";
import { cloneDeep } from "lodash";
import { Input, Select, Checkbox, DatePicker, Button } from "ant-design-vue";
import CustomPicker from "@/components/pickers/CustomPicker.vue";
import SupsisButton from "@/views/Pages/Settings/components/SupsisButton.vue";
import LinkedRecordFilterButton from "./LinkedRecordFilterButton.vue";
import { mapState } from "vuex";

export default Vue.extend({
    name: "TableFilter",
    components: {
        "a-input": Input,
        "a-select": Select,
        "a-select-option": Select.Option,
        "a-checkbox": Checkbox,
        "a-date-picker": DatePicker,
        "a-button": Button,
        CustomPicker,
        SupsisButton,
        LinkedRecordFilterButton,
    },
    props: {
        fields: {
            type: Array,
            required: true,
        },
        value: {
            type: Array,
            default: () => [],
        },
        records: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            clone: [],
            linkedRecordMap: {},
            searchQuery: "",
            fieldSuggestions: {},
            dateTemplates: [
                { label: this.$t("message.today"), value: "template_today" },
                { label: this.$t("message.yesterday"), value: "template_yesterday" },
                { label: this.$t("message.thisWeek"), value: "template_this_week" },
                { label: this.$t("message.lastWeek"), value: "template_last_week" },
                { label: this.$t("message.thisMonth"), value: "template_this_month" },
                { label: this.$t("message.lastMonth"), value: "template_last_month" },
                { label: this.$t("message.thisYear"), value: "template_this_year" },
                { label: this.$t("message.lastYear"), value: "template_last_year" },
            ],
        };
    },
    watch: {
        value: {
            immediate: true,
            handler(newValue) {
                this.clone = cloneDeep(newValue);
            },
        },
    },
    computed: {
        availableFields() {
            return this.fields.map((field) => {
                return {
                    ...field,
                    icon: field.icon || this.getFieldIcon(field._id),
                };
            });
        },
        ...mapState({
            users: (state) => state.user.users.data,
            tables: (state) => state.table.tables,
        }),
    },
    methods: {
        cancel() {
            this.$emit("close");
        },
        getFieldById(fieldId) {
            return this.fields.find((f) => f._id === fieldId);
        },
        addFilter() {
            // Modern UX: Varsayılan alan ve operatör otomatik seçilsin
            const firstField = (this.fields || [])[0];
            const defaultOperator = firstField
                ? this.getDefaultOperatorForField(firstField)
                : null;
            const newFilter = {
                fieldId: firstField ? firstField._id : null,
                operator: defaultOperator,
                value: null,
            };
            this.clone.push(newFilter);
            this.clone = _.cloneDeep(this.clone);
        },
        removeFilter(index) {
            this.clone.splice(index, 1);
            this.clone = _.cloneDeep(this.clone);
        },
        onOperatorChange(filter, index) {
            // Modern UX: Değer mümkünse korunur ve yeni operatöre göre dönüştürülür
            const previousValue = filter.value;
            const expectsArray = ["between", "is_one_of", "is_not_one_of"].includes(filter.operator);

            if (filter.operator === "between") {
                filter.value = Array.isArray(previousValue) ? previousValue : [];
            } else if (filter.operator === "is_one_of" || filter.operator === "is_not_one_of") {
                if (Array.isArray(previousValue)) {
                    filter.value = previousValue;
                } else if (previousValue || previousValue === 0 || previousValue === false) {
                    filter.value = [previousValue];
                } else {
                    filter.value = [];
                }
            } else {
                // Tekil değere geçiliyorsa array'in ilk elemanını al
                filter.value = Array.isArray(previousValue) ? previousValue[0] : previousValue;
            }

            this.clone[index] = _.cloneDeep(filter);
            this.clone = _.cloneDeep(this.clone);
        },
        onFieldChange(filter, index) {
            // Modern UX: Alan değişince mantıklı bir varsayılan operatör seç
            const field = this.fields.find((f) => f._id === filter.fieldId);
            filter.operator = field ? this.getDefaultOperatorForField(field) : null;
            // Değeri sıfırlama: yeni operatöre göre formatı belirle
            if (["between", "is_one_of", "is_not_one_of"].includes(filter.operator)) {
                filter.value = [];
            } else {
                filter.value = null;
            }
            this.clone[index] = _.cloneDeep(filter);
            this.clone = _.cloneDeep(this.clone);
        },
        getDefaultOperatorForField(field) {
            if (!field) return null;
            if (field.fieldType === "dropdown" || field.fieldType === "select") return "is_one_of";
            const valueType = field.valueType;
            if (valueType === "string") return "contains";
            if (valueType === "number") return "equal";
            if (valueType === "date") return "equal";
            if (valueType === "boolean") return "equal";
            return "equal";
        },
        isDropdownField(filter) {
            const field = this.fields.find((f) => f._id === filter.fieldId);
            if (!field) return false;

            // Normal dropdown kontrolü
            if (field.fieldType === "dropdown" || field.fieldType === "select") {
                return true;
            }

            // Lookup field için hedef alanın dropdown olup olmadığını kontrol et
            if (field.fieldType === "lookup") {
                const targetField = this.getTargetField(field);
                if (targetField) {
                    return (
                        targetField.fieldType === "dropdown" ||
                        targetField.fieldType === "checkbox" ||
                        targetField.valueType === "boolean"
                    );
                }
            }

            return false;
        },
        isUserField(filter) {
            const field = this.fields.find((f) => f._id === filter.fieldId);
            if (!field) return false;
            return field.fieldType === "user";
        },
        isMultipleDropdownField(filter) {
            const field = this.fields.find((f) => f._id === filter.fieldId);
            if (!field) return false;

            // Normal dropdown field için multiple kontrolü
            if (field.fieldType === "dropdown" || field.fieldType === "select") {
                return this.$_.get(field, "options.dropdown.multiple", false);
            }

            // Lookup field için hedef alanın multiple dropdown olup olmadığını kontrol et
            if (field.fieldType === "lookup") {
                const targetField = this.getTargetField(field);
                if (targetField && targetField.fieldType === "dropdown") {
                    return this.$_.get(targetField, "options.dropdown.multiple", false);
                }
            }

            return false;
        },
        getOperators(filter) {
            console.log(`getOperators`, filter);
            if (!filter.fieldId) return [];
            const field = this.fields.find((f) => f._id === filter.fieldId);
            const valueType = field.valueType;
            const fieldType = field.fieldType;
            console.log(`getOperators`, { field, valueType });
            const operators = {
                string: [
                    { label: this.$t("message.contains"), value: "contains" },
                    { label: this.$t("message.equals"), value: "equal" },
                    { label: this.$t("message.notEquals"), value: "not_equal" },
                    { label: this.$t("message.startsWith"), value: "starts_with" },
                    { label: this.$t("message.endsWith"), value: "ends_with" },
                    { label: this.$t("message.isOneOf"), value: "is_one_of" },
                    { label: "Bunları İçermeyen", value: "is_not_one_of" },
                    { label: this.$t("message.empty"), value: "empty" },
                    { label: this.$t("message.notEmpty"), value: "not_empty" },
                ],
                number: [
                    { label: this.$t("message.equals"), value: "equal" },
                    { label: this.$t("message.notEquals"), value: "not_equal" },
                    { label: this.$t("message.greaterThan"), value: "greater_than" },
                    { label: this.$t("message.lessThan"), value: "less_than" },
                    { label: this.$t("message.empty"), value: "empty" },
                    { label: this.$t("message.notEmpty"), value: "not_empty" },
                ],
                date: [
                    { label: this.$t("message.before"), value: "before" },
                    { label: this.$t("message.after"), value: "after" },
                    { label: this.$t("message.equals"), value: "equal" },
                    { label: this.$t("message.notEquals"), value: "not_equal" },
                    { label: this.$t("message.between"), value: "between" },
                    { label: this.$t("message.empty"), value: "empty" },
                    { label: this.$t("message.notEmpty"), value: "not_empty" },
                ],
                boolean: [
                    { label: this.$t("message.equals"), value: "equal" },
                    { label: this.$t("message.notEquals"), value: "not_equal" },
                ],
                asset: [
                    { label: this.$t("message.empty"), value: "empty" },
                    { label: this.$t("message.notEmpty"), value: "not_empty" },
                ],
                dropdown: [
                    { label: this.$t("message.contains"), value: "contains" },
                    { label: this.$t("message.equals"), value: "equal" },
                    { label: this.$t("message.notEquals"), value: "not_equal" },
                    { label: this.$t("message.isOneOf"), value: "is_one_of" },
                    { label: "Bunları İçermeyen", value: "is_not_one_of" },
                    { label: this.$t("message.empty"), value: "empty" },
                    { label: this.$t("message.notEmpty"), value: "not_empty" },
                ],
                array: [
                    { label: this.$t("message.contains"), value: "contains" },
                    { label: this.$t("message.equals"), value: "equal" },
                    { label: this.$t("message.notEquals"), value: "not_equal" },
                    { label: this.$t("message.isOneOf"), value: "is_one_of" },
                    { label: "Bunları İçermeyen", value: "is_not_one_of" },
                    { label: this.$t("message.empty"), value: "empty" },
                    { label: this.$t("message.notEmpty"), value: "not_empty" },
                ],
                json: [
                    { label: this.$t("message.empty"), value: "empty" },
                    { label: this.$t("message.notEmpty"), value: "not_empty" },
                ],
                linkedRecord: [
                    { label: this.$t("message.equals"), value: "equal" },
                    { label: this.$t("message.notEquals"), value: "not_equal" },
                    { label: this.$t("message.empty"), value: "empty" },
                    { label: this.$t("message.notEmpty"), value: "not_empty" },
                ],
                user: [
                    { label: this.$t("message.equals"), value: "equal" },
                    { label: this.$t("message.notEquals"), value: "not_equal" },
                    { label: this.$t("message.empty"), value: "empty" },
                    { label: this.$t("message.notEmpty"), value: "not_empty" },
                ],
                mixed: [
                    { label: this.$t("message.contains"), value: "contains" },
                    { label: this.$t("message.equals"), value: "equal" },
                    { label: this.$t("message.notEquals"), value: "not_equal" },
                    { label: this.$t("message.greaterThan"), value: "greater_than" },
                    { label: this.$t("message.lessThan"), value: "less_than" },
                    { label: this.$t("message.isOneOf"), value: "is_one_of" },
                    { label: "Bunları İçermeyen", value: "is_not_one_of" },
                    { label: this.$t("message.empty"), value: "empty" },
                    { label: this.$t("message.notEmpty"), value: "not_empty" },
                ],
                objectId: [
                    { label: this.$t("message.equals"), value: "equal" },
                    { label: this.$t("message.notEquals"), value: "not_equal" },
                    { label: this.$t("message.empty"), value: "empty" },
                    { label: this.$t("message.notEmpty"), value: "not_empty" },
                ],
            };

            const fallback = [
                { label: this.$t("message.empty"), value: "empty" },
                { label: this.$t("message.notEmpty"), value: "not_empty" },
            ];
            console.log(`getOperators last`, {
                operators,
                fallback,
                valueType,
                result: operators[valueType] || fallback,
            });
            if(valueType === "objectId"){
                return operators.objectId;
            }

            if (field.fieldType === "linked-record") {
                return operators.linkedRecord;
            }

            if (field.fieldType === "user") {
                return operators.user;
            }

            // Lookup alanları için hedef alanın tipine göre operatör belirle
            if (field.fieldType === "lookup") {
                const targetField = this.getTargetField(field);
                if (targetField) {
                    console.log("Lookup target field found:", targetField);
                    // Hedef alanın tipine göre uygun operatörleri döndür
                    return targetField.valueType === "mixed"
                        ? operators.mixed
                        : operators[targetField.valueType] || fallback;
                }
                // Lookup için uygun hedef alan bulunamazsa varsayılan mixed operatörlerini kullan
                return operators.mixed;
            }

            return operators[valueType] || fallback;
        },
        getValueInputComponent(filter) {
            if (!filter.fieldId) return "a-input";

            const field = this.fields.find((f) => f._id === filter.fieldId);
            const fieldType = field.fieldType;
            const valueType = field.valueType;

            if (fieldType === "linked-record" && !["empty", "not_empty"].includes(filter.operator)) {
                return "LinkedRecordFilterButton";
            }

            if (fieldType === "user" && !["empty", "not_empty"].includes(filter.operator)) {
                return "CustomPicker";
            }

            if (fieldType === "formula") {
                // Formula alanları için değişken tipine göre değer komponenti
                // Varsayılan olarak metin alanı kullan
                return "a-input";
            }

            // Lookup alanları için hedef alanın tipine göre değer komponenti belirle
            if (fieldType === "lookup" && !["empty", "not_empty"].includes(filter.operator)) {
                const targetField = this.getTargetField(field);
                if (targetField) {
                    console.log("getValueInputComponent - target field:", targetField);

                    // Hedef alanın tipine göre uygun input komponentini döndür
                    if (targetField.valueType === "date" || targetField.fieldType === "datetime") {
                        return "a-date-picker";
                    } else if (targetField.valueType === "boolean" || targetField.fieldType === "checkbox") {
                        return "a-select";
                    } else if (targetField.fieldType === "dropdown") {
                        return "a-select";
                    } else {
                        return "a-input";
                    }
                }
                // Hedef alan bulunamazsa varsayılan olarak metin alanı kullan
                return "a-input";
            }

            if (fieldType === "checkbox" || valueType === "boolean") {
                return "a-select";
            } else if (fieldType === "dropdown" || fieldType === "select") {
                return "a-select";
            } else if (valueType === "date") {
                return "a-date-picker";
            } else if (["is_one_of", "is_not_one_of"].includes(filter.operator) && valueType === "string" && fieldType !== "dropdown") {
                return "a-select";
            } else {
                return "a-input";
            }
        },
        getFieldOptions(filter) {
            const field = this.fields.find((f) => f._id === filter.fieldId);
            if (!field) return [];
            console.log(`getFieldOptions`, field);

            if (field.fieldType === "checkbox" || field.valueType === "boolean") {
                return [
                    { label: "True", value: true },
                    { label: "False", value: false },
                ];
            } else if (field.fieldType === "dropdown" || field.fieldType === "select") {
                // field.options şeklinde varsayıyorum
                const options = this.$_.get(field, "options.dropdown.options", []) || [];
                console.log(`getFieldOptions`, options);
                return options.map((opt) => ({ label: opt.value, value: opt.value }));
            }
            // Lookup alanları için hedef alan dropdown ise seçenekleri göster
            else if (field.fieldType === "lookup") {
                const targetField = this.getTargetField(field);
                if (targetField) {
                    // Hedef alan dropdown ise seçenekleri döndür
                    if (targetField.fieldType === "dropdown") {
                        const targetOptions = this.$_.get(targetField, "options.dropdown.options", []) || [];
                        return targetOptions.map((opt) => ({ label: opt.value, value: opt.value }));
                    }
                    // Hedef alan checkbox/boolean ise true/false seçeneklerini döndür
                    else if (targetField.fieldType === "checkbox" || targetField.valueType === "boolean") {
                        return [
                            { label: "True", value: true },
                            { label: "False", value: false },
                        ];
                    }
                }
                return [];
            } else {
                return [];
            }
        },
        applyFilter() {
            this.$emit("input", this.clone);
            this.$emit("close");
        },
        getSelectedFieldName(fieldId) {
            const field = this.fields.find((f) => f._id === fieldId);
            return field ? field.name : "";
        },
        getFieldIcon(fieldId) {
            const field = this.fields.find((f) => f._id === fieldId);
            if (!field) return "help_outline";

            switch (field.fieldType) {
                case "text":
                case "long-text":
                    return "text_fields";
                case "number":
                    return "looks_one";
                case "currency":
                    return "attach_money";
                case "datetime":
                    return "event";
                case "dropdown":
                    return "arrow_drop_down";
                case "checkbox":
                    return "check_box";
                case "url":
                    return "link";
                case "email":
                    return "email";
                case "phone":
                    return "phone";
                case "json":
                    return "code";
                case "file":
                    return "attachment";
                case "image":
                    return "image";
                case "video":
                    return "videocam";
                case "audio":
                    return "audiotrack";
                case "color":
                    return "palette";
                case "automation-button":
                    return "smart_button";
                case "contact":
                    return "person";
                case "ticket":
                    return "confirmation_number";
                case "task":
                    return "assignment";
                case "event":
                    return "event";
                case "form-submission":
                    return "assignment_turned_in";
                case "user":
                    return "support_agent";
                case "linked-record":
                    return "switch_access_shortcut";
                case "ai":
                    return "auto_awesome";
                case "conversation":
                    return "message";
                case "record_id":
                    return "tag";
                case "record_created_at":
                    return "add_circle_outline";
                case "record_updated_at":
                    return "update";
                case "record_created_by":
                    return "person_add";
                case "record_updated_by":
                    return "person_outline";
                case "formula":
                    return "functions";
                case "lookup":
                    return "sync_alt";
                case "auto-increment":
                    return "plus_one";
                default:
                    return field.icon || "help_outline";
            }
        },
        onLinkedRecordSelected(record, filter) {
            this.$set(this.linkedRecordMap, filter.fieldId, record);
            filter.value = record._id;
            this.clone = _.cloneDeep(this.clone);
        },
        selectDateTemplate(templateValue, index) {
            this.clone[index].value = templateValue;
            this.clone = _.cloneDeep(this.clone);
            document.querySelector(".filter-row .ant-calendar-picker span")?.click();
        },
        // valueType=string ve fieldType !== dropdown kontrolü
        isStringNonDropdownField(filter) {
            const field = this.getFieldById(filter.fieldId);
            if (!field) return false;
            return field.valueType === "string" && field.fieldType !== "dropdown";
        },
        // Lookup field için hedef alanı (target field) bulan yardımcı fonksiyon
        getTargetField(lookupField) {
            if (!lookupField || lookupField.fieldType !== "lookup") return null;

            const lookupOptions = lookupField.options && lookupField.options.lookup;
            if (!lookupOptions) return null;

            const linkedRecordFieldId = lookupOptions.linkedRecordField;
            const targetFieldId = lookupOptions.targetField;

            // İlgili linked-record alanını bul
            const linkedRecordField = this.fields.find((f) => f._id === linkedRecordFieldId);
            if (!linkedRecordField || !linkedRecordField.options || !linkedRecordField.options.linkedRecord)
                return null;

            const linkedTableId = linkedRecordField.options.linkedRecord.table;

            // Bağlı tablodaki hedef alanı bul
            const linkedTable = this.tables.find((t) => t._id === linkedTableId);
            if (!linkedTable) return null;

            const targetField = linkedTable.fields.find((f) => f._id === targetFieldId);
            return targetField || null;
        },

        // String field'lar için önerileri getir
        getFieldSuggestions(filter) {
            const field = this.getFieldById(filter.fieldId);
            if (!field || !this.records || this.records.length === 0) return [];

            // Cache key oluştur
            const cacheKey = `${field._id}_${this.searchQuery}`;
            if (this.fieldSuggestions[cacheKey]) {
                return this.fieldSuggestions[cacheKey];
            }

            // Records'dan bu field'ın değerlerini topla
            const values = new Set();
            this.records.forEach(record => {
                const value = record.data && record.data[field._id];
                if (value && typeof value === 'string' && value.trim()) {
                    values.add(value.trim());
                }
            });

            // Değerleri array'e çevir ve filtrele
            let suggestions = Array.from(values);
            
            // Arama query'si varsa filtrele
            if (this.searchQuery && this.searchQuery.trim()) {
                const query = this.searchQuery.toLowerCase();
                suggestions = suggestions.filter(value => 
                    value.toLowerCase().includes(query)
                );
            }

            // Uzunluğa göre sırala (kısa olanlar önce)
            suggestions.sort((a, b) => a.length - b.length);

            // En fazla 10 öneri göster
            suggestions = suggestions.slice(0, 10);

            // Label ve value formatında döndür
            const formattedSuggestions = suggestions.map(value => ({
                value: value,
                label: this.truncateText(value, 50),
                fullValue: value
            }));

            // Cache'e kaydet
            this.$set(this.fieldSuggestions, cacheKey, formattedSuggestions);

            return formattedSuggestions;
        },

        // Arama yapıldığında çağrılır
        onSearchSuggestions(searchValue) {
            this.searchQuery = searchValue;
            // Cache'i temizle ki yeni arama sonuçları gelsin
            this.fieldSuggestions = {};
        },

        // Metni belirtilen uzunlukta kes ve ellipsis ekle
        truncateText(text, maxLength) {
            if (!text || text.length <= maxLength) return text;
            return text.substring(0, maxLength - 3) + '...';
        },
    },
});
</script>

<style scoped lang="scss">
.customer-table-filter {
    display: flex;
    flex-direction: column;
    gap: 12px;

    .filter-conditions {
        display: flex;
        flex-direction: column;
        gap: 8px;

        .filter-row {
            display: grid;
            grid-template-columns: 260px 220px 1fr 48px; // field sabit, op sabit, value esneyen, delete sabit
            align-items: center;
            gap: 8px;
            padding: 8px;
            border: 1px solid var(--color-border-default);
            border-radius: 6px;
            background: var(--color-bg-neutral-default);

            .ant-select,
            .custom-picker,
            .ant-calendar-picker,
            .ant-input {
                width: 100% !important;
            }
        }
    }

    .custom-picker {
        width: 100% !important;
    }

    .filter-actions {
        margin-top: 8px;
        display: flex;
        gap: 8px;
        justify-content: space-between;
        .right {
            display: flex;
            gap: 8px;
        }
    }
}
.date-template-label {
    border: 1px solid #22272d;
    background-color: transparent;
    color: var(--default-text-color);
    border-radius: 5px;
    cursor: pointer;
    gap: 4px;
    padding: 5px 10px;
    height: 35px;
    display: flex;
    align-items: center;
    justify-content: center;
}
.date-template-label {
    border: 1px solid #22272d;
    background-color: transparent;
    color: var(--default-text-color);
    border-radius: 5px;
    cursor: pointer;
    gap: 4px;
    padding: 5px 10px;
    height: 35px;
    display: flex;
    align-items: center;
    justify-content: center;
}
</style>
<style lang="scss">
.ant-calendar-picker-input {
    padding: 0px !important;
}
.ant-calendar-ok-btn {
    color: white !important;
}
</style>
